import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { WhiteLogo, arrowFullscreen,Chaser } from "../../assets/images/index";
import './chat-messages.scss'
import MathJaxForChatPage from '../../components/common/mathjaxforchatpage';
import Button from "../button";
import { useLayoutContext } from "../../layouts/general-layout"; // Import context
import { RouterConstant } from "../../routers/index"; // Import RouterConstant
import ZoomChartModal from "../../components/zoomable-image";
import { TrackingService } from  "../../services/tracking/tracking.service";
import { useExerciseStore } from "../../utils/exerciseStore";
import { useQuestionsStore } from "../../utils/questionStore"; // Import the zustand store
import { useUserStore } from "../../utils/userStore"; // Import the user store
import { ExerciseQuestionsService } from "../../services/exercise-questions/exercise-questions.service";

interface Message {
  text: string;
  isBot: boolean;
  role: "system" | "user" | "assistant";
  static_helper_text?: boolean;
  timestamp: Date;
}

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  onShowSolution?: () => void;
  onDisplayQuestion?: () => void;
  allStepsSolution: boolean;
  showQuestionList: boolean;
  scrollRef?: React.RefObject<HTMLDivElement>;
  handlefurtherExplanation?: (answer: "Yes" | "No") => void;
  isSelectedQuestion: boolean;
  showConfirmationButtons: boolean
  nextStep: boolean;
  handleNextStep: () => void;
  allStepsCompleted: boolean;
  handleExplainSolution?: () => void;
  allQuestionsCompleted: boolean;
  isSolutionByImage?: boolean;
  handleUpsertExerciseHistory: (index?: number, is_completed?: boolean) => Promise<void>;
  onMessageRendered?: (index: number, message: Message) => void;
  isFromSelfTestAnswerPreview?: boolean;
}

export default function Index(
  { messages, isLoading, onShowSolution, onDisplayQuestion, allStepsSolution, handlefurtherExplanation, showQuestionList, scrollRef, isSelectedQuestion, showConfirmationButtons, nextStep, handleNextStep, allStepsCompleted, handleExplainSolution,allQuestionsCompleted, isSolutionByImage, handleUpsertExerciseHistory, onMessageRendered, isFromSelfTestAnswerPreview }: ChatMessagesProps
) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { t } = useLayoutContext(); // Use the context instead of useTranslation
  const navigate = useNavigate();
  const trackingServiceInstance = new TrackingService();
  const exerciseService = new ExerciseQuestionsService();
  const exerciseFilename = useExerciseStore((s) => s.selectedExerciseName);
  const { ROUTERS_PATH } = RouterConstant;
  const [zoomImageSrc, setZoomImageSrc] = React.useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isChoosingExercise, setIsChoosingExercise] = useState(false);
  const [messageStatus, setMessageStatus] = useState<Record<number, "typing" | "rendered">>({});
  const prevMessagesRef = useRef<Message[]>([]);
  const userInfo = useUserStore((state) => state.userInfo);
  const selectedExerciseId = useExerciseStore.getState().selectedExerciseId;
  const selectedQuestionId = useQuestionsStore.getState().currentSelectedQuestion;
  const hasCalledHistoryForNewExercise = useRef(false);
  const hasHandledListButton = useRef(false);
  const shouldShowListButton = (showQuestionList || allStepsCompleted) && !allQuestionsCompleted;
  const isUserScrollingRef = useRef(false);


  useEffect(() => {
    setMessageStatus((prev) => {
      const next = { ...prev };
  
      messages.forEach((msg, idx) => {
        // Force all previous messages to be rendered when a new message arrives
        if (idx < messages.length - 1) {
          if (next[idx] !== "rendered") {
            next[idx] = "rendered";
          }
          return;
        }

        const prevMsg = prevMessagesRef.current[idx];
        const sameText = prevMsg && prevMsg.text === msg.text;
  
        if (sameText && prev[idx]) return;
        if (!prevMsg) {
          if (!msg.isBot || msg.text.includes("<img") || msg.static_helper_text) {
            next[idx] = "rendered";
          } else {
            next[idx] = "typing";
          }
        }
      });
  
      Object.keys(next).forEach((k) => {
        if (Number(k) >= messages.length) delete next[k];
      });
  
      prevMessagesRef.current = messages.slice();
      return next;
    });
  }, [messages]);

  
  useEffect(() => {
    const scrollElement = scrollRef?.current;
    if (!scrollElement) return;

    let isScrollingProgrammatically = false;
    let lastScrollTop = 0;
    let touchStartY = 0;
    
    const handleScroll = () => {
      if (isScrollingProgrammatically) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      if (scrollTop < lastScrollTop) {
        isUserScrollingRef.current = true;
      } else if (distanceFromBottom < 10) {
        isUserScrollingRef.current = false;
      }
      
      lastScrollTop = scrollTop;
    };

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touchY = e.touches[0].clientY;
      if (touchY > touchStartY) {
        // Swiping down = scrolling up
        isUserScrollingRef.current = true;
      }
    };

    (scrollElement as any).__scrollProgrammatically = () => {
      isScrollingProgrammatically = true;
      setTimeout(() => { isScrollingProgrammatically = false; }, 300);
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    scrollElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollElement.addEventListener('touchmove', handleTouchMove, { passive: true });
    
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      scrollElement.removeEventListener('touchstart', handleTouchStart);
      scrollElement.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);




  const handleMessageRendered = (index: number) => {
    setMessageStatus((prev: any) => {
      const next = { ...prev, [index]: "rendered" };

      for (let i = index + 1; i < messages.length; i++) {
        const m = messages[i];
        if (m && m.isBot && m.static_helper_text) {
          next[i] = "rendered";
        } else {
          break;
        }
      }
      setTimeout(scrollToBottom, 50);
      if (typeof onMessageRendered === "function") {
        onMessageRendered(index, messages[index]);
      }
      return next;
    });
  };

  const lastMessageRendered = messages.length === 0 ? true : messageStatus[messages.length - 1] === "rendered";

  useEffect(() => {
    if (nextStep && !allStepsCompleted) {
      setTimeout(scrollToBottom, 100); 
      const currentStep = useQuestionsStore.getState().currentSelectedStep;
      if (currentStep) {
       //handleUpsertExerciseHistory({ is_completed: true });
      }
    }
  }, [nextStep, allStepsCompleted]);

  useEffect(() => {
    if (allStepsCompleted && !allQuestionsCompleted) {
      const currentStep = useQuestionsStore.getState().currentSelectedStep;
      if (currentStep) {
       handleUpsertExerciseHistory({ is_completed: true });
      }
    }
  }, [allStepsCompleted]);

  useEffect(() => {
    if (allQuestionsCompleted && !hasCalledHistoryForNewExercise.current) {
      //handleUpsertExerciseHistory({ is_completed: true });
      handleQuestionHistory();        
      hasCalledHistoryForNewExercise.current = true;
      handleTracking();
    }
  }, [allQuestionsCompleted]);

  useEffect(() => {
    if (!shouldShowListButton || hasHandledListButton.current) return;
    hasHandledListButton.current = true;
  
    (async () => {
      try {
        await handleQuestionHistory();
      } catch (err) {
        console.error("handleQuestionHistory failed", err);
      }
    })();
  }, [shouldShowListButton]);

  const handleTracking = () => {
    trackingServiceInstance.trackEvent("execrise_completed", {
      source: "chatpage",
      selected_exercise: exerciseFilename || "",
      });
  }

  const getUserInitials = () => {
    const firstName = userInfo?.first_name;
    const lastName = userInfo?.last_name;
    if (!firstName && !lastName) return "U";
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };


  const handleQuestionHistory = async () => {
    if (!selectedExerciseId || !selectedQuestionId) {
      console.warn("No selected exercise or question");
      navigate(ROUTERS_PATH.CHOOSEEXERCISE);
      return;
    }
  
    try {       
      useQuestionsStore.getState().addCompletedQuestion(
        selectedExerciseId,
        selectedQuestionId
      );
    } catch (error) {
      console.error("Error updating exercise history", error);
    }
  }

  const handleChartClick = (imgHtml: string) => {
    const match = imgHtml.match(/src=["']([^"']+)["']/);
    if (match) setZoomImageSrc(match[1]);
  };

  const scrollToBottom = () => {
    const lastMsg = messages[messages.length - 1];
    if (isUserScrollingRef.current && !lastMsg?.static_helper_text) return;
    
    if (scrollRef?.current) {
      (scrollRef.current as any).__scrollProgrammatically?.();
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    } else if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };


  useEffect(() => {
    if (!isLoading && lastMessageRendered) {
      setTimeout(scrollToBottom, 100);
    }
  }, [lastMessageRendered, isLoading, showConfirmationButtons]);

  return (
    <div className="chat-page-body" ref={scrollRef}>
      <div className="chat-page-time" />
      {messages.map((message, index) => {
        const status = messageStatus[index];
        if (message.isBot && message.static_helper_text) {
          let shouldShow = true;
          for (let i = index - 1; i >= 0; i--) {
            if (!messages[i].static_helper_text) {
              shouldShow = messageStatus[i] === "rendered";
              break;
            }
          }
          
          if (!shouldShow) {
            return null;
          }
        }
        return (
          <div
            key={index}
            className={`message-box ${
              message.role === "system" && !message.isBot
                ? "message-box--system-error"
                : message.isBot
                ? "message-box--bot"
                : "message-box--user"
            }`}
          >
            <div className="message-box-chaser-container">
              {message.role === "user" ? (
                <div className="user-initials-avatar">{getUserInitials()}</div>
              ) : (
                <img src={Chaser} alt="Bot" className="message-box-chaser-image" />
              )}
            </div>
              <div className="message-box-text">
                {message.isBot ? (
                  message.text.includes("<img") ? (
                    <div
                      className="chat-image-block"
                      onClick={() => handleChartClick(message.text)}
                    >
                      <img
                        src={arrowFullscreen}
                        alt="Arrow Fullscreen"
                        className="fullscreen-icon"
                        title={t("enlarge")}
                      />
                      <div
                        className="chat-image-wrapper"
                        dangerouslySetInnerHTML={{ __html: message.text }}
                      />
                    </div>
                  ) : message.static_helper_text ? (
                    (() => {
                      // Find the previous non-static message
                      let shouldShow = true;
                      for (let i = index - 1; i >= 0; i--) {
                        if (!messages[i].static_helper_text) {
                          shouldShow = messageStatus[i] === "rendered";
                          break;
                        }
                      }
                      
                      return shouldShow ? (
                        <div className="static-helper-text">
                          <span>{message.text}</span>
                        </div>
                      ) : null;
                    })()
                  ) : (
                    <MathJaxForChatPage
                      content={message.text}
                      instant={status === "rendered"}
                      onRenderComplete={() => handleMessageRendered(index)}
                      onTypingProgress={scrollToBottom}
                      onAfterRender={scrollToBottom}

                    />
                  )
                ) : (
                  // user message
                  <span>{message.text}</span>
                )}

                <span className="timestamp">
                  {message.timestamp instanceof Date && !isNaN(message.timestamp.getTime())
                    ? message.timestamp.toLocaleTimeString()
                    : new Date().toLocaleTimeString()}
                </span>
              </div>
          </div>
        );
        
      })}

      {isLoading && (
        <div className="message-type bot-message">
          <div className="message-box-chaser-container">
            <img src={Chaser} alt="Bot" className="message-box-chaser-image" />
          </div>
          <div className="typing-indicator">
            <span className="typing-text">
              {t("typing")}
              <span className="typing-dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Buttons show only when the last message is fully rendered */}
      {!isLoading && ( isSelectedQuestion || isFromSelfTestAnswerPreview ) && lastMessageRendered && (
        <div className="chat-actions inner-chat-btn">
          {showConfirmationButtons || (allStepsSolution && !showQuestionList) ? (
            <>
              <Button
                text={t("yes")}
                color="green-small"
                onClick={() => handlefurtherExplanation?.("Yes")}
              />
              <Button text={t("no")} color="orange" onClick={() => handlefurtherExplanation?.("No")} />
            </>
          ) : showQuestionList || allStepsCompleted ? (
            <>
              {allQuestionsCompleted ? (
                <Button
                  text={t("selectNewExercise")}
                  color="black"
                  onClick={() => navigate(ROUTERS_PATH.CHOOSEEXERCISE)}
                  loader={isChoosingExercise}
                />
              ) : (
                <Button
                  text={t("ListOfSections")}
                  color="black"
                  onClick={onDisplayQuestion}
                  loader={isCompleting}
                />
              )}
            </>
          ) : (
            <>
              {nextStep && !allStepsCompleted ? (
                <Button text={t("nextStep")} color="black" onClick={handleNextStep} />
              ) : (
                <>
                  {onShowSolution && !allStepsCompleted && (
                    <Button text={t("ShowTheSolution")} color="black" onClick={onShowSolution} />
                  )}
                  {!allStepsCompleted && (
                    <Button
                      text={t("ExpandTheExplanation")}
                      color="black"
                      onClick={handleExplainSolution}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
      {!isLoading && isSolutionByImage && lastMessageRendered && (
        <div className="chat-actions inner-chat-btn">
          <Button text={t("selectNewExercise")} color="black" onClick={() => navigate(ROUTERS_PATH.CHOOSEEXERCISE)} />
        </div>
      )}

      {zoomImageSrc && <ZoomChartModal imageSrc={zoomImageSrc} onClose={() => setZoomImageSrc(null)} />}

      <div ref={messagesEndRef} />
    </div>
  );
}